import { skipToken } from "@tanstack/react-query";
import type { RouterOutputs } from "@remi/api";
import { FIVE_MINUTES_MS, ONE_MINUTE_MS } from "@remi/constants";
import { remiLogger } from "@remi/logging";
import { RemiLink } from "@remi/next-ui";
import { can } from "@remi/permissions";

import { isAddressUnitRequiredError } from "~/utils/address-unit-required";
import { api } from "~/utils/api";
import { getAppointmentPrecheckMessage } from "~/utils/appointment-precheck-error";
import { getQueryConfig } from "~/utils/query-config";
import { display, displayToast } from "~/components/ui/sonner";
import { useUserProfile } from "~/contexts/UserProfileContext";
import useNavigation from "./useNavigation";

const FIVE_SECONDS_MS = 5 * 1000;

export function useBid(bidId?: number | null, enabled = true) {
  const { user } = useUserProfile();
  const allowed = can.bid.readAnyScope(user);
  const query = api.bid.getBid.useQuery(allowed && bidId ? bidId : skipToken, { enabled, staleTime: 1000 * 5 });
  return { ...query, isDenied: !allowed };
}

export type BidById = RouterOutputs["bid"]["getBidById"];

/**
 * @deprecated should be removed in favor of useBid where only the necessary data is fetched
 * */
export function useBidDetailed(bidId?: number) {
  return api.bid.getBidById.useQuery(bidId && bidId !== -1 ? bidId : skipToken, { staleTime: 1000 * 5 });
}

export const useBidUpdate = () => {
  const { bid: bidRoute, project: projectRoute } = api.useUtils();
  const update = api.bid.update.useMutation({
    async onMutate({ id: bidId, ...updatedBid }) {
      // Bid by Id
      await bidRoute.getBid.cancel(bidId);
      const previousValueById = bidRoute.getBid.getData(bidId);
      if (previousValueById) {
        // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 property ordering differs from tRPC output type in optimistic update
        bidRoute.getBid.setData(bidId, {
          ...previousValueById,
          ...updatedBid,
          bidProject: {
            ...previousValueById.bidProject,
          },
          bidData: {
            ...previousValueById.bidData,
            ...updatedBid.bidData,
            eagleViewStatus: updatedBid.bidData?.eagleViewStatus ?? previousValueById.bidData.eagleViewStatus,
          },
        } as never);
      }

      return { previousValueById };
    },
    onError(_, variables, context) {
      if (context?.previousValueById) {
        bidRoute.getBid.setData(variables.id, context.previousValueById);
      }
    },
    async onSettled(_data, _error, input) {
      await Promise.all([
        projectRoute.byId.invalidate(),
        bidRoute.getBid.invalidate(input.id),
        bidRoute.getBidById.invalidate(input.id),
        bidRoute.getSearchResults.invalidate(),
      ]);
    },
  });
  return update;
};

export const useBidMerge = () => {
  const { navigate } = useNavigation();
  const { bid: bidRoute, person: personRoute } = api.useUtils();

  const merge = api.bid.merge.useMutation({
    onSuccess: async (bid) => {
      await Promise.all([
        bidRoute.getBidById.invalidate(bid.id),
        bidRoute.getWorkSiteById.invalidate({ id: bid.worksite.id }),
        personRoute.byId.invalidate(bid.worksite.homeownerId),
      ]);
      display.success("Bids merged successfully");
      setTimeout(() => {
        // give time for the drawer to close properly before navigating. This could be optional if we want to navigate immediately
        void navigate({ pathname: `/dashboard/bids/${bid.id}` });
      }, 300);
    },
    onError: (error) => {
      remiLogger.error("An error occurred while merging the bids", { error });
      display.error("Error merging bids", { description: error.message });
    },
  });

  return merge;
};

export const useBidCreate = () => {
  const { bid: bidRoute } = api.useUtils();
  const create = api.bid.create.useMutation({
    async onSettled(bidData, _error, _input) {
      if (!bidData) {
        return;
      }
      displayToast({
        title: "Bid created successfully",
        variant: "secondary",
        link: (
          <RemiLink className="text-icon-accent" href={`/dashboard/bids/${bidData.bidId}`}>
            View bid
          </RemiLink>
        ),
      });
      await Promise.all([bidRoute.getBidById.invalidate(bidData.bidId), bidRoute.getSearchResults.invalidate()]);
    },
    onError: (error) => {
      if (!isAddressUnitRequiredError(error) && !getAppointmentPrecheckMessage(error)) {
        display.error("Failed to create bid", { description: error.message });
      }
    },
  });
  return create;
};

export type BidChildProject = RouterOutputs["bid"]["getChildProjects"][number];

export const useBidChildProjects = ({ bidId, showActiveOnly }: { bidId?: number | null; showActiveOnly?: boolean }) => {
  return api.bid.getChildProjects.useQuery(bidId ? { bidId, showActiveOnly } : skipToken, { staleTime: 1000 * 5 });
};

export const useExternalContractorInfo = (projectId?: number) => {
  return api.bid.getExternalContractorInfoForProject.useQuery(projectId ? { projectId } : skipToken, {
    staleTime: FIVE_SECONDS_MS,
  });
};

export type WorksiteBid = RouterOutputs["bid"]["getBidsByWorkSite"][number];

export const useBidsByWorkSite = ({ workSiteId }: { workSiteId?: number }) => {
  return api.bid.getBidsByWorkSite.useQuery(workSiteId ? { workSiteId } : skipToken, { staleTime: 1000 * 5 });
};

export const useBidPreNtpProjects = (input: { bidId: number } | typeof skipToken) => {
  const { user } = useUserProfile();
  return api.bid.getPreNtpProjects.useQuery(input !== skipToken && can.quote.read(user) ? input : skipToken);
};

export const useBidTasks = (bidId?: number) => {
  return api.bid.getTasks.useQuery(bidId ? bidId : skipToken, { staleTime: 1000 * 5, refetchOnWindowFocus: true });
};

export const useBidPanelMarkupRequirement = (bidProjectId?: number) => {
  return api.bid.getPanelMarkupRequirement.useQuery(bidProjectId ? { bidProjectId } : skipToken, {
    staleTime: FIVE_SECONDS_MS,
  });
};

export const useBidChildProjectProfitAndLossFacts = (bidProjectId?: number) => {
  return api.bid.getChildProjectProfitAndLossFacts.useQuery(bidProjectId ? { bidProjectId } : skipToken, {
    staleTime: FIVE_SECONDS_MS,
  });
};

export const useBidHomeownerStatusTimeline = (bidId?: number) => {
  return api.bid.getHomeownerStatusTimeline.useQuery(bidId ? { bidId } : skipToken, { staleTime: 1000 * 5 });
};

export const useBidRequestsForQuote = (bidId?: number) => {
  return api.bid.getRequestsForQuote.useQuery(bidId ? bidId : skipToken);
};

export const useDefaultShingleLineForBid = (bidId?: number, enabled = true) => {
  return api.bid.getDefaultShingleLineForBid.useQuery(bidId ? { bidId } : skipToken, {
    enabled,
    staleTime: ONE_MINUTE_MS,
  });
};

export const useSiteVisitConversionBidRequests = ({ bidId }: { bidId?: number }) => {
  return api.bid.getSiteVisitConversionBidRequests.useQuery(bidId ? bidId : skipToken, { staleTime: 1000 * 5 });
};

export const useBidOwners = (bidId?: number) => {
  return api.bid.getOwners.useQuery(bidId ? bidId : skipToken);
};

const ACCOUNT_MANAGER_CONTACT_QUERY_CONFIG = getQueryConfig(FIVE_MINUTES_MS);

export const useBidAccountManagerContact = ({ bidId }: { bidId?: number }) => {
  return api.bid.getAccountManagerContact.useQuery(bidId ?? skipToken, ACCOUNT_MANAGER_CONTACT_QUERY_CONFIG);
};

export const useBidOrganizations = (bidId?: number) => {
  return api.bid.getOrganizations.useQuery(bidId ? bidId : skipToken, { staleTime: 1000 * 5 });
};

export const useCompletionOfWorkData = (projectId?: number | null) => {
  return api.project.getCompletionOfWorkData.useQuery(projectId ? { projectId } : skipToken);
};

export const useBidOwnerUpdate = () => {
  const { bid: bidRoute, project: projectRoute, projectOwner: projectOwnerRoute } = api.useUtils();
  return api.bid.updateOwner.useMutation({
    async onMutate({ bidId, personId, role }) {
      await bidRoute.getOwners.cancel(bidId);
      const previousValueById = bidRoute.getOwners.getData(bidId);
      if (previousValueById) {
        const owners = previousValueById.filter((owner) => owner.role !== role);
        if (personId) {
          owners.push({
            personId,
            role,
            substitutePersonId: null,
            person: {
              firstName: "",
              lastName: "",
              disabled: false,
              primaryEmailAddress: null,
              primaryPhone: null,
              profilePhoto: null,
              contact: { id: "", name: "" },
            },
          });
        }
        bidRoute.getOwners.setData(bidId, owners);
      }
      return { previousValueById };
    },
    onError(_, variables, context) {
      if (context?.previousValueById) {
        bidRoute.getOwners.setData(variables.bidId, context.previousValueById);
      }
    },
    async onSettled(_data, _error, input) {
      await Promise.all([
        bidRoute.getOwners.invalidate(input.bidId),
        bidRoute.getSearchResults.invalidate(),
        input.projectId && projectRoute.byId.invalidate(input.projectId),
        input.projectId && projectOwnerRoute.getByProjectId.invalidate({ projectId: input.projectId }),
      ]);
    },
  });
};
